<template>
  <!-- Rendered into <body> via the mounted() hook below so it escapes any
       overflow:hidden / transformed ancestor (portal lesson, how-to §12).
       The root carries BOTH `calm__modal` (chrome) and `calm` (so --c-*
       tokens resolve for any Kit components used in the slots). -->
  <transition name="calm-modal-fade">
    <div
      v-if="show"
      class="calm__modal calm"
      :class="[`calm__modal--${size}`, { 'is-open': show }]"
      role="dialog"
      aria-modal="true"
      :aria-label="title || undefined"
      @mousedown.self="onOverlayMousedown"
      @click.self="onOverlayClick"
    >
      <div class="calm__modal__box" role="document">
        <div class="calm__modal__head">
          <slot name="header">
            <h3 class="calm__modal__title">{{ title }}</h3>
          </slot>
          <button
            type="button"
            class="calm__modal__close"
            aria-label="Close"
            @click="close"
          >
            &times;
          </button>
        </div>

        <div class="calm__modal__body">
          <slot></slot>
        </div>

        <div v-if="hasFooter" class="calm__modal__actions">
          <slot name="footer" :close="close" :confirm="confirm">
            <button type="button" class="c-btn c-btn--ghost" @click="close">
              {{ cancelText }}
            </button>
            <button type="button" class="c-btn c-btn--primary" @click="confirm">
              {{ confirmText }}
            </button>
          </slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "calm-modal",
  props: {
    show: { type: Boolean, default: false },        // use with .sync
    title: { type: String, default: "" },
    size: {
      type: String,
      default: "md",
      validator: (v) => ["sm", "md", "lg"].includes(v),
    },
    closeOnOverlay: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: true },
    cancelText: { type: String, default: "ยกเลิก" },
    confirmText: { type: String, default: "ยืนยัน" },
  },
  data() {
    return { overlayMousedown: false, teleported: false };
  },
  computed: {
    hasFooter() {
      // render the actions row if a footer slot is provided OR default footer enabled
      return this.showFooter || !!this.$slots.footer || !!this.$scopedSlots.footer;
    },
  },
  watch: {
    show(val) {
      this.toggleBodyScroll(val);
      // Teleport on OPEN. With `<transition><div v-if="show">`, while the modal
      // is closed `this.$el` is the transition's placeholder COMMENT node
      // (nodeType 8), so a teleport attempt in mounted() is a no-op and the
      // modal would otherwise render inline forever. Once `show` flips true the
      // real <div> exists after the next tick — hoist it then.
      if (val) {
        this.$nextTick(this.teleportToBody);
      } else {
        // On close, `v-if` destroys the real <div> and restores the inline
        // comment placeholder; the next open creates a brand-new element at the
        // original location, so allow it to be re-hoisted.
        this.teleported = false;
      }
    },
  },
  mounted() {
    // If the modal mounts already-open, `$el` is the real element now and we
    // can hoist immediately; otherwise the `show` watcher handles it on open.
    this.teleportToBody();
    document.addEventListener("keydown", this.onKeydown);
    if (this.show) this.toggleBodyScroll(true);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    this.toggleBodyScroll(false);
    // clean up the element we hoisted into <body>
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
  methods: {
    // Move the rendered root to <body> so the fixed overlay can't be clipped by
    // an overflow:hidden / transformed ancestor and the scrim covers the whole
    // viewport. Guarded so a real element is hoisted at most once (re-running on
    // every open is a harmless no-op once `teleported` is set).
    teleportToBody() {
      if (this.teleported) return;
      const el = this.$el;
      if (el && el.nodeType === 1) {
        document.body.appendChild(el);
        this.teleported = true;
      }
    },
    close() {
      this.$emit("update:show", false);
      this.$emit("close");
    },
    confirm() {
      this.$emit("confirm");
    },
    onOverlayMousedown() {
      // track that the press STARTED on the scrim, so a drag that ends on the
      // scrim (text-selection release) doesn't spuriously close the modal
      this.overlayMousedown = true;
    },
    onOverlayClick() {
      if (this.closeOnOverlay && this.overlayMousedown) {
        this.close();
      }
      this.overlayMousedown = false;
    },
    onKeydown(e) {
      if (this.show && this.closeOnEsc && e.key === "Escape") {
        e.stopPropagation();
        this.close();
      }
    },
    toggleBodyScroll(lock) {
      // lightweight scroll-lock; counts nested modals via a data attribute
      const b = document.body;
      const n = parseInt(b.getAttribute("data-calm-modal-open") || "0", 10);
      const next = lock ? n + 1 : Math.max(0, n - 1);
      b.setAttribute("data-calm-modal-open", String(next));
      b.style.overflow = next > 0 ? "hidden" : "";
    },
  },
};
</script>

<style scoped>
/* The visual chrome lives in calm.scss (.calm__modal*). Only the
   enter/leave transition for the overlay is local. */
.calm-modal-fade-enter-active,
.calm-modal-fade-leave-active {
  transition: opacity 180ms cubic-bezier(0.32, 0.72, 0.32, 1);
}
.calm-modal-fade-enter,
.calm-modal-fade-leave-to {
  opacity: 0;
}
</style>
